import { useEffect } from 'react';
import { Form, Input, Select, DatePicker, Switch, Row, Col, App } from 'antd';
import Tabs from '../../../components/common/LegacyTabs';
import { trpc } from '../../../lib/api';
import dayjs, { type Dayjs } from 'dayjs';
import type { Company } from '../../../types/company';
import { withDevModalFilePath, NumericInput, PhoneInput, PostalCodeInput, EmailInput } from '../../../components/common';
import { katakanaRule, phoneNumberRule, postalCodeRule } from '@/constants/validation';
import { useFormAutoBackup } from '../../../hooks/useFormAutoBackup';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/pages/admin/companies/CompanyModal.tsx');
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const COMPANY_SUBSCRIPTION_PLANS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'standard', label: 'Standard' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'enterprise_premium', label: 'Enterprise Premium' },
] as const;
const COMPANY_SUBSCRIPTION_STATUSES = [
  { value: 'trialing', label: '無料登録' },
  { value: 'pending_payment', label: '入金待ち' },
  { value: 'active', label: 'アクティブ' },
  { value: 'past_due', label: '支払遅延' },
  { value: 'canceled', label: 'キャンセル済' },
  { value: 'suspended', label: '停止中' },
] as const;

type CompanySubscriptionPlan = (typeof COMPANY_SUBSCRIPTION_PLANS)[number]['value'];
type CompanySubscriptionStatus = (typeof COMPANY_SUBSCRIPTION_STATUSES)[number]['value'];

type CompanyFormValues = Omit<Partial<Company>, 'establishedDate' | 'subscriptionStartsAt' | 'subscriptionEndsAt'> & {
  establishedDate?: Dayjs | string | Date | null;
  subscriptionStartsAt?: Dayjs | string | Date | null;
  subscriptionEndsAt?: Dayjs | string | Date | null;
  displayId?: string;
};

interface CompanyModalProps {
  open: boolean;
  company: CompanyFormValues | null;
  onClose: () => void;
  onSuccess: () => void;
}

const toDateValue = (value: Dayjs | string | Date | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (dayjs.isDayjs(value)) {
    return value.toDate();
  }

  const normalized = dayjs(value);
  return normalized.isValid() ? normalized.toDate() : undefined;
};

export const CompanyModal: React.FC<CompanyModalProps> = ({
  open,
  company,
  onClose,
  onSuccess
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<CompanyFormValues>();
  const isEdit = !!company;

  // フォーム自動バックアップ（新規作成のみ有効）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({
    formType: 'company',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: open && !isEdit,
  });

  // 会社作成
  const createMutation = trpc.company.create.useMutation({
    onSuccess: () => {
      message.success('会社を作成しました');
      form.resetFields();
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`作成に失敗しました: ${error.message}`);
    }
  });

  // 会社更新
  const updateMutation = trpc.company.update.useMutation({
    onSuccess: () => {
      message.success('会社情報を更新しました');
      form.resetFields();
      clearBackup(); // 保存成功時にバックアップをクリア
      onSuccess();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    }
  });

  useEffect(() => {
    if (open && company) {
      form.setFieldsValue({
        ...company,
        establishedDate: company.establishedDate ? dayjs(company.establishedDate) : undefined,
        subscriptionStartsAt: company.subscriptionStartsAt ? dayjs(company.subscriptionStartsAt) : undefined,
        subscriptionEndsAt: company.subscriptionEndsAt ? dayjs(company.subscriptionEndsAt) : undefined,
      });
    } else {
      form.resetFields();
    }
  }, [open, company, form]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  const handleSubmit = (values: CompanyFormValues) => {
    const formData = {
      name: values.name,
      nameKana: values.nameKana,
      representativeName: values.representativeName,
      representativeTitle: values.representativeTitle,
      establishedDate: toDateValue(values.establishedDate),
      capital:
        values.capital !== undefined && values.capital !== null && values.capital !== ''
          ? String(values.capital)
          : undefined,
      employeeCount: values.employeeCount,
      fiscalYearStart: values.fiscalYearStart,
      industry: values.industry,
      businessDescription: values.businessDescription,
      clientType: values.clientType,
      funcOptions: values.funcOptions,
      postalCode: values.postalCode,
      prefecture: values.prefecture,
      city: values.city,
      address: values.address,
      building: values.building,
      phone: values.phone,
      fax: values.fax,
      email: values.email,
      website: values.website,
      billingEmail: values.billingEmail,
      billingAddress: values.billingAddress,
      taxNumber: values.taxNumber,
      subscriptionPlan: values.subscriptionPlan as CompanySubscriptionPlan | undefined,
      subscriptionStatus: values.subscriptionStatus as CompanySubscriptionStatus | undefined,
      subscriptionStartsAt: toDateValue(values.subscriptionStartsAt),
      subscriptionEndsAt: toDateValue(values.subscriptionEndsAt),
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!company?.id) {
        message.error('更新対象の会社IDが見つかりません');
        return;
      }
      updateMutation.mutate({
        id: company.id,
        data: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <Modal
      title={isEdit ? '会社情報編集' : '新規会社登録'}
      open={open}
      forceRender
      onCancel={onClose}
      width={800}
      onOk={() => form.submit()}
      confirmLoading={getMutationPendingState(createMutation) || getMutationPendingState(updateMutation)}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Tabs defaultActiveKey="basic">
          <TabPane tab="基本情報" key="basic">
            <Form.Item
              name="name"
              label="会社名"
              rules={[
                { required: true, message: '会社名を入力してください' },
                { max: 200, message: '200文字以内で入力してください' }
              ]}
            >
              <Input placeholder="株式会社サンプル" />
            </Form.Item>

            <Form.Item
              name="nameKana"
              label="会社名カナ"
              rules={[
                { max: 200, message: '200文字以内で入力してください' },
                katakanaRule(),
              ]}
            >
              <Input placeholder="カブシキガイシャサンプル" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="representativeName"
                  label="代表者名"
                >
                  <Input placeholder="山田 太郎" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="representativeTitle"
                  label="代表者役職"
                >
                  <Input placeholder="代表取締役" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="establishedDate"
                  label="設立日"
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="capital"
                  label="資本金"
                >
                  <NumericInput
                    style={{ width: '100%' }}
                    formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value!.replace(/\u00a5\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="employeeCount"
                  label="従業員数"
                >
                  <NumericInput style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="fiscalYearStart"
                  label="会計年度開始月"
                >
                  <Select placeholder="選択してください">
                    {[...Array(12)].map((_, i) => (
                      <Option key={i + 1} value={i + 1}>{i + 1}月</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="industry"
                  label="業種"
                >
                  <Input placeholder="製造業" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="businessDescription"
              label="事業内容"
            >
              <TextArea rows={4} placeholder="事業内容を入力してください" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="clientType"
                  label="クライアントタイプ"
                >
                  <Input placeholder="original / bm" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="funcOptions"
                  label="機能オプション"
                >
                  <Select
                    mode="tags"
                    tokenSeparators={[',', ' ']}
                    placeholder="bm, option2"
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="住所・連絡先" key="contact">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="postalCode"
                  label="郵便番号"
                  rules={[postalCodeRule()]}
                >
                  <PostalCodeInput placeholder="123-4567" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="prefecture"
                  label="都道府県"
                >
                  <Input placeholder="東京都" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="city"
                  label="市区町村"
                >
                  <Input placeholder="港区" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="address"
              label="住所"
            >
              <Input placeholder="六本木1-2-3" />
            </Form.Item>

            <Form.Item
              name="building"
              label="建物名"
            >
              <Input placeholder="サンプルビル 5F" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="電話番号"
                  rules={[
                    { max: 20, message: '電話番号は20文字以内で入力してください' },
                    phoneNumberRule(),
                  ]}
                >
                  <PhoneInput placeholder="03-1234-5678" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fax"
                  label="FAX番号"
                  rules={[
                    { max: 20, message: 'FAX番号は20文字以内で入力してください' },
                    phoneNumberRule('有効なFAX番号を入力してください'),
                  ]}
                >
                  <PhoneInput placeholder="03-1234-5679" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="メールアドレス"
                  rules={[
                    { type: 'email', message: '正しいメールアドレスを入力してください' }
                  ]}
                >
                  <EmailInput placeholder="info@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="website"
                  label="ウェブサイト"
                  rules={[
                    { type: 'url', message: '正しいURLを入力してください' }
                  ]}
                >
                  <Input placeholder="https://example.com" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="請求情報" key="billing">
            <Form.Item
              name="billingEmail"
              label="請求先メールアドレス"
              rules={[
                { type: 'email', message: '正しいメールアドレスを入力してください' }
              ]}
            >
              <EmailInput placeholder="billing@example.com" />
            </Form.Item>

            <Form.Item
              name="billingAddress"
              label="請求先住所"
            >
              <TextArea rows={3} placeholder="請求先が異なる場合のみ入力" />
            </Form.Item>

            <Form.Item
              name="taxNumber"
              label="税務番号"
            >
              <Input placeholder="T1234567890123" />
            </Form.Item>
          </TabPane>

          <TabPane tab="契約プラン" key="subscription">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="subscriptionPlan"
                  label="プラン"
                  initialValue="free"
                >
                  <Select>
                    {COMPANY_SUBSCRIPTION_PLANS.map((plan) => (
                      <Option key={plan.value} value={plan.value}>{plan.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="subscriptionStatus"
                  label="ステータス"
                  initialValue="trialing"
                >
                  <Select>
                    {COMPANY_SUBSCRIPTION_STATUSES.map((status) => (
                      <Option key={status.value} value={status.value}>{status.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="subscriptionStartsAt"
                  label="契約プラン開始日"
                >
                  <DatePicker style={{ width: '100%' }} showTime />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="subscriptionEndsAt"
                  label="契約プラン終了日"
                >
                  <DatePicker style={{ width: '100%' }} showTime />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isActive"
                  label="状態"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="有効" unCheckedChildren="無効" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

